import React, { useState, useEffect } from 'react';
import {
  Modal,
  Calendar,
  Badge,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Divider,
  List,
  Avatar,
  Statistic,
  Spin,
  Empty,
  Tabs,
  Timeline,
  Tooltip,
} from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  ExperimentOutlined,
  FieldTimeOutlined,
  UserOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { get_batch_calendar_data } from '@/utils/api';
import moment from 'moment';
import './BatchCalendarModal.css';
import StatCard from '@/components/StatCard';
import { Play, Calendar as CalendarIcon, CheckCircle } from 'lucide-react';
import { formatDate, formatDateTime, toAppMoment } from '@/utils/datetime';

const { Title, Text, Paragraph } = Typography;

/**
 * BatchCalendarModal Component
 * Pharma-compliant batch calendar showing running, upcoming, and completed batches
 * Enhanced design with modern UI/UX
 */
const BatchCalendarModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [calendarData, setCalendarData] = useState({
    running_batches: [],
    upcoming_batches: [],
    completed_batches: [],
  });
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedBatches, setSelectedBatches] = useState([]);

  useEffect(() => {
    if (open) {
      fetchCalendarData();
    }
  }, [open]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      await get_batch_calendar_data((res) => {
        if (res?.data?.status === 'success') {
          const data = res.data.data;
          setCalendarData({
            running_batches: data.running_batches || [],
            upcoming_batches: data.upcoming_batches || [],
            completed_batches: data.completed_batches || [],
          });
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setLoading(false);
    }
  };

  // Get batches for a specific date
  const getBatchesForDate = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const allBatches = [
      ...calendarData.running_batches,
      ...calendarData.upcoming_batches,
      ...calendarData.completed_batches,
    ];

    return allBatches.filter((batch) => {
      const batchDate = toAppMoment(batch.scheduled_date || batch.start_date)?.format('YYYY-MM-DD');
      return batchDate === dateStr;
    });
  };

  // Calendar cell render with enhanced design
  const dateCellRender = (value) => {
    const batches = getBatchesForDate(value);
    const isToday = value.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');

    return (
      <div
        className="batch-calendar-cell"
        style={{
          minHeight: '60px',
          padding: '4px',
          background: isToday ? 'rgba(47, 89, 95, 0.05)' : 'transparent',
          borderRadius: '4px',
        }}
      >
        {batches.slice(0, 3).map((batch, index) => (
          <Tooltip
            key={index}
            title={
              <div>
                <div style={{ fontWeight: 600 }}>{batch.batch_number}</div>
                <div style={{ fontSize: 12 }}>{batch.recipe_name}</div>
                <div style={{ fontSize: 11, opacity: 0.9 }}>
                  {formatDateTime(batch.scheduled_date || batch.start_date, 'HH:mm')}
                </div>
              </div>
            }
            placement="right"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginBottom: '2px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: getBatchStatus(batch.status).bgColor,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              className="batch-cell-item"
            >
              <Badge status={getBatchStatus(batch.status).badgeStatus} />
              <Text
                ellipsis
                style={{
                  fontSize: 11,
                  maxWidth: 70,
                  color: getBatchStatus(batch.status).textColor,
                  fontWeight: 500,
                }}
              >
                {batch.batch_number}
              </Text>
            </div>
          </Tooltip>
        ))}
        {batches.length > 3 && (
          <Text style={{ fontSize: 10, color: '#8c8c8c', marginLeft: 6 }}>
            +{batches.length - 3} more
          </Text>
        )}
      </div>
    );
  };

  const getBatchStatus = (status) => {
    const statusMap = {
      running: {
        color: 'processing',
        badgeStatus: 'processing',
        icon: <SyncOutlined spin />,
        text: 'Running',
        bgColor: 'rgba(47, 89, 95, 0.1)',
        textColor: '#204448',
        borderColor: '#204448',
      },
      in_progress: {
        color: 'processing',
        badgeStatus: 'processing',
        icon: <SyncOutlined spin />,
        text: 'In Progress',
        bgColor: 'rgba(47, 89, 95, 0.1)',
        textColor: '#204448',
        borderColor: '#204448',
      },
      upcoming: {
        color: 'warning',
        badgeStatus: 'warning',
        icon: <ClockCircleOutlined />,
        text: 'Upcoming',
        bgColor: 'rgba(250, 173, 20, 0.1)',
        textColor: '#faad14',
        borderColor: '#faad14',
      },
      scheduled: {
        color: 'warning',
        badgeStatus: 'warning',
        icon: <ClockCircleOutlined />,
        text: 'Scheduled',
        bgColor: 'rgba(250, 173, 20, 0.1)',
        textColor: '#faad14',
        borderColor: '#faad14',
      },
      completed: {
        color: 'success',
        badgeStatus: 'success',
        icon: <CheckCircleOutlined />,
        text: 'Completed',
        bgColor: 'rgba(82, 196, 26, 0.1)',
        textColor: '#52c41a',
        borderColor: '#52c41a',
      },
      paused: {
        color: 'default',
        badgeStatus: 'default',
        icon: <WarningOutlined />,
        text: 'Paused',
        bgColor: 'rgba(140, 140, 140, 0.1)',
        textColor: '#8c8c8c',
        borderColor: '#8c8c8c',
      },
    };
    return statusMap[status?.toLowerCase()] || statusMap.upcoming;
  };

  const onDateSelect = (date) => {
    setSelectedDate(date);
    const batches = getBatchesForDate(date);
    setSelectedBatches(batches);
  };

  const renderBatchCard = (batch) => {
    const statusInfo = getBatchStatus(batch.status);

    return (
      <Card
        key={batch.batch_id || batch.batch_number}
        size="small"
        className="batch-detail-card"
        style={{
          marginBottom: 12,
          borderRadius: '12px',
          border: `1px solid ${statusInfo.borderColor}20`,
          borderLeft: `4px solid ${statusInfo.borderColor}`,
          background: statusInfo.bgColor,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          transition: 'all 0.3s ease',
        }}
        hoverable
      >
        <Row gutter={[16, 8]} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Space align="center" wrap>
                <Avatar
                  size={36}
                  style={{
                    background: statusInfo.bgColor,
                    border: `2px solid ${statusInfo.borderColor}`,
                  }}
                  icon={<ExperimentOutlined style={{ color: statusInfo.textColor }} />}
                />
                <div>
                  <Text strong style={{ fontSize: 16, display: 'block', lineHeight: 1.2 }}>
                    {batch.batch_number}
                  </Text>
                  <Tag
                    color={statusInfo.color}
                    icon={statusInfo.icon}
                    style={{
                      marginTop: 4,
                      borderRadius: '6px',
                      fontSize: 11,
                    }}
                  >
                    {statusInfo.text}
                  </Tag>
                </div>
              </Space>

              <Text
                type="secondary"
                style={{
                  fontSize: 13,
                  display: 'block',
                  marginTop: 4,
                }}
              >
                <strong>{batch.recipe_name}</strong> • {batch.product_name}
              </Text>

              <Space size="large" wrap style={{ marginTop: 8 }}>
                <Tooltip title="Scheduled Date">
                  <Space size={6}>
                    <CalendarOutlined style={{ color: '#595959', fontSize: 14 }} />
                    <Text style={{ fontSize: 13, color: '#262626' }}>
                      {formatDate(batch.scheduled_date || batch.start_date)}
                    </Text>
                  </Space>
                </Tooltip>

                <Tooltip title="Scheduled Time">
                  <Space size={6}>
                    <FieldTimeOutlined style={{ color: '#595959', fontSize: 14 }} />
                    <Text style={{ fontSize: 13, color: '#262626' }}>
                      {batch.scheduled_time || formatDateTime(batch.start_date, 'HH:mm')}
                    </Text>
                  </Space>
                </Tooltip>

                {batch.operator && (
                  <Tooltip title="Operator">
                    <Space size={6}>
                      <Avatar size={20} icon={<UserOutlined />} style={{ background: '#204448' }} />
                      <Text style={{ fontSize: 13, color: '#262626' }}>{batch.operator}</Text>
                    </Space>
                  </Tooltip>
                )}
              </Space>
            </Space>
          </Col>

          {batch.batch_size && (
            <Col>
              <Card
                size="small"
                style={{
                  borderRadius: '10px',
                  background: 'rgba(47, 89, 95, 0.05)',
                  border: '1px solid rgba(47, 89, 95, 0.2)',
                  textAlign: 'center',
                  minWidth: 110,
                }}
              >
                <Statistic
                  title={<Text style={{ fontSize: 11, color: '#8c8c8c' }}>Batch Size</Text>}
                  value={batch.batch_size}
                  suffix={batch.batch_unit || 'kg'}
                  valueStyle={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#204448',
                  }}
                />
              </Card>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  const renderRunningBatches = () => (
    <Card
      title={
        <Space>
          <Avatar
            size={32}
            style={{ background: 'rgba(47, 89, 95, 0.1)' }}
            icon={<SyncOutlined spin style={{ color: '#204448' }} />}
          />
          <div>
            <Text strong style={{ fontSize: 15 }}>
              Running Batches
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {calendarData.running_batches.length} active
            </Text>
          </div>
        </Space>
      }
      size="small"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}
      headStyle={{
        background: 'rgba(47, 89, 95, 0.05)',
        borderBottom: '2px solid rgba(47, 89, 95, 0.1)',
        borderRadius: '12px 12px 0 0',
      }}
    >
      {calendarData.running_batches.length === 0 ? (
        <Empty
          description={<Text type="secondary">No batches currently running</Text>}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '40px 0' }}
        />
      ) : (
        <List
          dataSource={calendarData.running_batches}
          renderItem={renderBatchCard}
          style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}
        />
      )}
    </Card>
  );

  const renderUpcomingBatches = () => (
    <Card
      title={
        <Space>
          <Avatar
            size={32}
            style={{ background: 'rgba(250, 173, 20, 0.1)' }}
            icon={<ClockCircleOutlined style={{ color: '#faad14' }} />}
          />
          <div>
            <Text strong style={{ fontSize: 18 }}>
              Upcoming Batches
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {calendarData.upcoming_batches.length} scheduled
            </Text>
          </div>
        </Space>
      }
      size="small"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}
      headStyle={{
        background: 'rgba(250, 173, 20, 0.05)',
        borderBottom: '2px solid rgba(250, 173, 20, 0.1)',
        borderRadius: '12px 12px 0 0',
      }}
    >
      {calendarData?.upcoming_batches.length === 0 ? (
        <Empty
          description={<Text type="secondary">No upcoming batches scheduled</Text>}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '40px 0' }}
        />
      ) : (
        <Timeline
          style={{
            maxHeight: 400,
            overflowY: 'auto',
            marginTop: 20,
            paddingLeft: '12px',
            paddingTop: '8px',
          }}
          items={calendarData.upcoming_batches.slice(0, 10).map((batch) => ({
            color: 'blue',
            dot: (
              <div
                style={{
                  background: '#fff',
                  border: '2px solid #204448',
                  borderRadius: '50%',
                  width: 14,
                  height: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    background: '#204448',
                    borderRadius: '50%',
                  }}
                />
              </div>
            ),
            children: (
              <Card
                size="small"
                style={{
                  marginBottom: 12,
                  borderRadius: '8px',
                  border: '1px solid rgba(47, 89, 95, 0.15)',
                  background: 'rgba(47, 89, 95, 0.02)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
              >
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text strong style={{ fontSize: 14 }}>
                    {batch.batch_number}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {batch.recipe_name}
                  </Text>
                  <Space size={8} style={{ marginTop: 4 }}>
                    <CalendarOutlined style={{ color: '#204448', fontSize: 12 }} />
                    <Text style={{ fontSize: 12, color: '#595959' }}>
                      {formatDateTime(batch.scheduled_date)}
                    </Text>
                  </Space>
                </Space>
              </Card>
            ),
          }))}
        />
      )}
    </Card>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="95vw"
      zIndex={9999}
      style={{ top: 50, padding: 4 }}
      bodyStyle={{
        height: 'calc(90vh - 110px)',
        overflowY: 'auto',
        background: '#f5f7fa',
        padding: '24px',
      }}
      footer={null}
      className="batch-calendar-modal"
      title={
        <div>
          <Space size={16}>
            <Avatar
              size={48}
              style={{
                background: '#204448',
                boxShadow: '0 4px 12px rgba(47, 89, 95, 0.3)',
              }}
              icon={<CalendarOutlined style={{ fontSize: 24 }} />}
            />
            <div className="p-3">
              <Title level={3} style={{ margin: 0, lineHeight: 1.2, color: 'white' }}>
                Batch Production Calendar
              </Title>
              <Space size={8} style={{ marginTop: 4 }}>
                <Tag
                  color="blue"
                  style={{
                    borderRadius: '6px',
                    fontSize: 14,
                    padding: '2px 10px',
                  }}
                >
                  21 CFR Part 11 Compliant
                </Tag>
                <Tag
                  color="green"
                  style={{
                    borderRadius: '6px',
                    fontSize: 14,
                    padding: '2px 10px',
                  }}
                >
                  GMP Validated
                </Tag>
              </Space>
            </div>
          </Space>
        </div>
      }
    >
      <Spin spinning={loading}>
        {/* Enhanced Statistics Row */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          {/* Running Batches */}
          <Col span={8}>
            <StatCard
              icon={<Play size={24} color="#204448" />}
              label="Running Batches"
              value={calendarData.running_batches.length}
              bgColor="#e8f0f1"
              iconColor="#204448"
            />
          </Col>

          {/* Upcoming Batches */}
          <Col span={8}>
            <StatCard
              icon={<CalendarIcon size={24} color="#faad14" />}
              label="Upcoming Batches"
              value={calendarData.upcoming_batches.length}
              bgColor="#fff7e6"
              iconColor="#faad14"
            />
          </Col>

          {/* Completed Today */}
          <Col span={8}>
            <StatCard
              icon={<CheckCircle size={24} color="#52c41a" />}
              label="Completed Today"
              value={
                calendarData.completed_batches.filter(
                  (b) => toAppMoment(b.end_date)?.format('YYYY-MM-DD') === toAppMoment(new Date()).format('YYYY-MM-DD')
                ).length
              }
              bgColor="#f6ffed"
              iconColor="#52c41a"
            />
          </Col>
        </Row>

        {/* Main Content */}
        <Row gutter={16}>
          {/* Calendar */}
          <Col span={16}>
            <Card
              title={
                <Space>
                  <CalendarOutlined style={{ fontSize: 18, color: '#204448' }} />
                  <Text strong style={{ fontSize: 16 }}>
                    Production Schedule Calendar
                  </Text>
                </Space>
              }
              style={{
                height: '100%',
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                background: '#fff',
              }}
              headStyle={{
                borderBottom: '2px solid #f0f0f0',
                padding: '16px 24px',
              }}
              bodyStyle={{ padding: '16px' }}
            >
              <Calendar
                fullscreen={false}
                dateCellRender={dateCellRender}
                onSelect={onDateSelect}
                style={{
                  borderRadius: '8px',
                }}
              />

              {/* Selected Date Details */}
              {selectedBatches.length > 0 && (
                <Card
                  size="small"
                  title={
                    <Space>
                      <Avatar
                        size={28}
                        style={{ background: '#204448' }}
                        icon={<CalendarOutlined />}
                      />
                      <Text strong>Batches on {selectedDate.format('DD MMMM YYYY')}</Text>
                      <Tag color="blue">{selectedBatches.length}</Tag>
                    </Space>
                  }
                  style={{
                    marginTop: 20,
                    borderRadius: '10px',
                    border: '1px solid rgba(47, 89, 95, 0.2)',
                    background: 'rgba(47, 89, 95, 0.02)',
                  }}
                  headStyle={{
                    borderBottom: '1px solid rgba(47, 89, 95, 0.1)',
                  }}
                >
                  <List
                    dataSource={selectedBatches}
                    renderItem={renderBatchCard}
                    style={{ maxHeight: 300, overflowY: 'auto' }}
                  />
                </Card>
              )}
            </Card>
          </Col>

          {/* Side Panel */}
          <Col span={8}>
            <Tabs
              defaultActiveKey="1"
              style={{
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                padding: '8px',
              }}
              tabBarStyle={{
                marginBottom: 8,
                paddingLeft: 8,
              }}
            >
              <Tabs.TabPane
                tab={
                  <Space>
                    <SyncOutlined spin style={{ color: '#204448' }} />
                    <Text strong>Running</Text>
                    <Tag color="blue" style={{ borderRadius: '10px', fontSize: 11 }}>
                      {calendarData.running_batches.length}
                    </Tag>
                  </Space>
                }
                key="1"
              >
                {renderRunningBatches()}
              </Tabs.TabPane>
              <Tabs.TabPane
                tab={
                  <Space>
                    <ClockCircleOutlined style={{ color: '#faad14' }} />
                    <Text strong>Upcoming</Text>
                    <Tag color="orange" style={{ borderRadius: '10px', fontSize: 11 }}>
                      {calendarData.upcoming_batches.length}
                    </Tag>
                  </Space>
                }
                key="2"
              >
                {renderUpcomingBatches()}
              </Tabs.TabPane>
            </Tabs>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

export default BatchCalendarModal;
